import { ErrorBoundary, Suspense } from "@suspensive/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import {
  LabelExplorer,
  LabelExplorerLoadingFallback,
} from "@/components/analysis/label-explorer";
import { MessagesDrawer } from "@/components/analysis/messages-drawer";
import { SessionDrawer } from "@/components/analysis/session-drawer";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import { Card, CardContent } from "@/components/ui/card";
import type { AnalysisFilters } from "@/types/analysis";

interface MessagesDrawerState {
  title: string;
  description?: string;
  filters: AnalysisFilters;
}

export default function AnalysisPage() {
  const [filters] = useState<AnalysisFilters>({});
  const memoFilters = useMemo(() => filters, [filters]);

  const [messagesDrawer, setMessagesDrawer] =
    useState<MessagesDrawerState | null>(null);
  const [sessionContext, setSessionContext] = useState<{
    sessionId: string;
    highlightMessageId: string;
  } | null>(null);

  const openMessages = useCallback((state: MessagesDrawerState) => {
    setMessagesDrawer(state);
  }, []);

  const handleMessagesOpenChange = useCallback((open: boolean) => {
    if (!open) setMessagesDrawer(null);
  }, []);

  const handleSessionOpenChange = useCallback((open: boolean) => {
    if (!open) setSessionContext(null);
  }, []);

  const handleOpenSession = useCallback(
    (sessionId: string, messageId: string) => {
      setSessionContext({ sessionId, highlightMessageId: messageId });
    },
    [],
  );

  const handleTopClick = useCallback(
    (top: string) => {
      openMessages({
        title: `의도: ${top}`,
        description: "해당 top 카테고리에 속한 user 메시지",
        filters: { ...memoFilters, intentTop: top },
      });
    },
    [memoFilters, openMessages],
  );

  const handleSubClick = useCallback(
    (leaf: string) => {
      openMessages({
        title: `의도: ${leaf}`,
        description: "해당 라벨이 부여된 user 메시지",
        filters: { ...memoFilters, intent: leaf },
      });
    },
    [memoFilters, openMessages],
  );

  const handleNegativeClick = useCallback(
    (label: string) => {
      openMessages({
        title: `부정 신호: ${label}`,
        description: "해당 부정 라벨이 부여된 user 메시지",
        filters: { ...memoFilters, negativeFeedbackLabel: label },
      });
    },
    [memoFilters, openMessages],
  );

  const handlePositiveClick = useCallback(
    (label: string) => {
      openMessages({
        title: `긍정 신호: ${label}`,
        description: "해당 긍정 라벨이 부여된 user 메시지",
        filters: { ...memoFilters, positiveFeedbackLabel: label },
      });
    },
    [memoFilters, openMessages],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          채팅 메시지 분석
        </h1>
        <p className="text-muted-foreground leading-normal">
          사용자 메시지의 의도·감성 라벨 분포를 조회합니다.
        </p>
      </div>

      <Card>
        <CardContent className="px-4 py-4 md:px-6 md:py-6">
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<LabelExplorerLoadingFallback />}>
                  <LabelExplorer
                    filters={memoFilters}
                    onTopClick={handleTopClick}
                    onSubClick={handleSubClick}
                    onNegativeClick={handleNegativeClick}
                    onPositiveClick={handlePositiveClick}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>

      <MessagesDrawer
        title={messagesDrawer?.title ?? ""}
        description={messagesDrawer?.description}
        filters={messagesDrawer?.filters ?? null}
        onOpenChange={handleMessagesOpenChange}
        onOpenSession={handleOpenSession}
      />

      <SessionDrawer
        sessionId={sessionContext?.sessionId ?? null}
        highlightMessageId={sessionContext?.highlightMessageId ?? null}
        onOpenChange={handleSessionOpenChange}
      />
    </div>
  );
}
